import thunk from 'redux-thunk';
import serverVars from 'server-vars';
import { DESKTOP, MOBILE, TABLET } from 'dibs-buyer-layout/exports/deviceTypeEnums.ts';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants.ts';

/* Reducers */
import { defaultReducers } from '../../reducers/reducerUtils.js';
import { pdpRespMakeOfferModalReducer as makeOfferModal } from '../../reducers/pdpRespMakeOfferModalReducer.ts';
import { pdpRespNotificationsReducer as sharedNotificationsReducer } from '../../reducers/pdpRespNotificationsReducer.js';

/* Components */
import { RelayEnvironmentProvider } from 'react-relay';
import { SocketContextProvider } from 'dibs-subscriptions-utils/exports/SocketContext.ts';
import { ServerVarsContextProvider } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpRespMarketplaceLayout } from '../../pdp/respMarketplace/PdpRespMarketplaceLayout/PdpRespMarketplaceLayout.tsx';

/* Helpers */
import { hydrateRoot } from 'react-dom/client';
import { initializeDBL } from 'dibs-buyer-layout/src/entries/index.jsx';
import { getItemIdFromUri } from '../../utils/getItemIdFromUri.ts';
import { clientRelayEnvironment } from '../../clientRelayEnvironment.ts';
import { setDatadogABTestValues } from 'dibs-ab-tests/exports/clientAbTestV2.ts';
import { pdpRespPrintReducer as pdpPrint } from '../../reducers/pdpRespPrintReducer.js';
import { datadog, initSharedDatadogRum, pdpTypes } from 'dibs-datadog/exports/datadog.ts';
import { perfMeasureFromPageInit } from 'dibs-tracking/src/prometheus/peformanceTracking.ts';
import { storePdpVisit } from '../../pdp/PdpSharedNotifications/pdpSharedNotificationHelpers.js';
import { getIsBotOnClient } from 'dibs-buyer-layout/exports/botHelpers.ts';
import { getIsPdpBottomNewSlModuleTestVariant } from '../../utils/abTestHelper.ts';

/* Actions */
import { setRelayVariables } from '../../actions/sharedRelayVariableActions.js';

/* Notifications */
import { pdpRespNotifications } from '../../pdp/PdpRespNotifications/pdpRespNotifications.js';

import { initializePrerenderedCheckout } from 'dibs-checkout/exports/prerender/initializePrerenderedCheckout.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { sharedLoginCheckSetUp } from '../../authentication/sharedLoginCheck.ts';
import { setIntlVariables } from '../../actions/sharedIntlVariablesActions.js';
import { setUserTypeTrade } from '../../actions/sharedMobileActions.js';

const isPdpRespPreviewPage = serverVars.get('isPdpRespPreviewPage');

/**
 * DBL has a different environment that's created in initializeDBL.
 */
const options = {
    autoRunNotificationQueue: !isPdpRespPreviewPage,
    middleware: [thunk],
    notifications: pdpRespNotifications,
    reducers: {
        pdpPrint,
        sharedNotificationsReducer,
        makeOfferModal,
        ...defaultReducers,
    },
};

const itemId = getItemIdFromUri(location.pathname);
if (itemId) {
    storePdpVisit({ itemId });
}

datadog.load().then(dd => {
    initSharedDatadogRum(dd, {
        device: 'resp',
        page: PAGE_TYPE.PDP,
        pageSubType: pdpTypes.marketplace,
    });
    setDatadogABTestValues();
});

initializeDBL(options).then(async ({ store }) => {
    perfMeasureFromPageInit({ subGroup: 'resp/init-dbl', monitoringGroup: 'unknown' });

    const { environment, socketListeners } = clientRelayEnvironment(
        serverVars.get('dbl.relayData')
    );
    sharedLoginCheckSetUp({ store, environment });

    const locale = serverVars.get('locale');
    const messages = serverVars.get('messages');
    const deviceType = serverVars.get('deviceType');
    const primaryRelayVariables = serverVars.get('relay.primaryRelayVariables');

    store.dispatch(setRelayVariables(primaryRelayVariables));
    store.dispatch(setIntlVariables(serverVars.get('intlVariables')));
    store.dispatch(setUserTypeTrade(serverVars.get('userType') === CUSTOMER_TYPE_TRADE));

    hydrateRoot(
        document.getElementById('js-root') as HTMLElement,
        <RelayEnvironmentProvider environment={environment}>
            <ServerVarsContextProvider
                isBot={getIsBotOnClient()}
                isMobile={deviceType === MOBILE}
                isTablet={deviceType === TABLET}
                isDesktop={deviceType === DESKTOP}
                isPdpRespPreviewPage={isPdpRespPreviewPage}
                areDisplayAdsEnabled={serverVars.get('areDisplayAdsEnabled')}
                isBannerAdsAbTestVariant={serverVars.get('isBannerAdsAbTestVariant')}
                isPdpSiderailSellerReviewCombinedVariant={serverVars.get(
                    'isPdpSiderailSellerReviewCombinedVariant'
                )}
                isPdpEstimateSimilarSoldTestVariant={serverVars.get(
                    'isPdpEstimateSimilarSoldTestVariant'
                )}
                isPersonalizedCollectionTestVariant={serverVars.get(
                    'isPersonalizedCollectionTestVariant'
                )}
                isItemFavoritingFeb2026Variant={serverVars.get('isItemFavoritingFeb2026Variant')}
                isItemFavoritingFeb2026Control={serverVars.get('isItemFavoritingFeb2026Control')}
                isPdpSimilarSoldItemsModuleVariant={serverVars.get(
                    'isPdpSimilarSoldItemsModuleVariant'
                )}
                isPdpBottomNewSlModuleTestVariant={getIsPdpBottomNewSlModuleTestVariant()}
                isPDPLargerItemsVariant={serverVars.get('isPDPLargerItemsVariant')}
            >
                <SocketContextProvider socketListeners={socketListeners}>
                    <PdpRespMarketplaceLayout
                        store={store}
                        locale={locale}
                        messages={messages}
                        primaryRelayVariables={primaryRelayVariables}
                        secondaryRelayVariables={serverVars.get('relay.secondaryRelayVariables')}
                    />
                </SocketContextProvider>
            </ServerVarsContextProvider>
        </RelayEnvironmentProvider>
    );

    if (itemId) {
        await initializePrerenderedCheckout({
            isMobile: false,
            itemId,
            locale,
            messages,
        });
    }
});
